import React, { useRef, useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'

interface ImageCarouselProps {
  images: string[]
  badge?: string
  currentIndex?: number
  onIndexChange?: (index: number) => void
  initialIndex?: number
  carouselHeight?: number
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  badge,
  currentIndex,
  onIndexChange,
  initialIndex = 0,
  carouselHeight = 240,
}) => {
  const swiperRef = useRef<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = React.useState<number>(currentIndex ?? initialIndex)

  useEffect(() => {
    if (typeof currentIndex !== 'number') return

    setActiveIndex(currentIndex)
    // loop 有効時は activeIndex がクローン込みの値になるため realIndex で比較し、
    // 移動もクローンを考慮する slideToLoop を使う。
    if (swiperRef.current && swiperRef.current.realIndex !== currentIndex) {
      swiperRef.current.slideToLoop(currentIndex)
    }
  }, [currentIndex])

  return (
    <div className="relative w-full overflow-hidden" style={{ height: `${carouselHeight}px` }}>
      {/* Badge */}
      {badge && (
        <img src={`/images/nurseries/${badge}.png`} alt={badge} className="absolute" style={{ width: '110px', zIndex: 20 }} />
      )}

      {/* Swiper inline styles to ensure CSS works without external CSS files */}
      <style>{`
        .nursery-swiper {
          width: 100%;
          height: 100%;
        }
        .nursery-swiper .swiper-wrapper {
          display: flex;
          height: 100%;
        }
        .nursery-swiper .swiper-slide {
          flex-shrink: 0;
          width: 100%;
          height: 100%;
        }
      `}</style>

      <Swiper
        className="nursery-swiper"
        loop={images.length > 1}
        initialSlide={currentIndex ?? initialIndex}
        onSwiper={(swiper) => {
          swiperRef.current = swiper
        }}
        onSlideChange={(swiper) => {
          // loop 有効時、activeIndex はクローンを含み最後の実スライドで固定されてしまう。
          // 実画像に対応するのは realIndex なので、ドット追従は realIndex を使う。
          setActiveIndex(swiper.realIndex)
          onIndexChange?.(swiper.realIndex)
        }}
      >
        {images.map((src, index) => (
          <SwiperSlide key={index}>
            <img
              src={src}
              alt={`Nursery image ${index + 1}`}
              style={{ width: '100%', height: `${carouselHeight}px`, objectFit: 'cover' }}
              loading="lazy"
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom Pagination Dots */}
      {/* NOTE: client/ は Tailwind の走査対象外(#2250)のため、ここで中央寄せに使う
          left-1/2 / -translate-x-1/2 / transform は app/views で未使用だと CSS が生成されない。
          脱Flowbite 前は node_modules/flowbite のグロブから偶然生成されていたが、Flowbite 削除で
          消えドットが左寄せになった。走査に依存せず確実に中央寄せするためインラインスタイルにする。 */}
      {images.length > 1 && (
        <div
          className="absolute flex"
          style={{ left: '50%', transform: 'translateX(-50%)', gap: '8px', zIndex: 10, bottom: '4px' }}
        >
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation()
                if (swiperRef.current) {
                  swiperRef.current.slideToLoop(index)
                }
              }}
              className="rounded-full transition-all hover:scale-125"
              style={{
                width: '8px',
                height: '8px',
                backgroundColor: activeIndex === index ? '#000000' : '#E5E7EB',
              }}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
